import { Avatar } from "@msuite/ui/avatar";
import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { Email } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { usePopoverContext } from "@msuite/ui/popover";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import { getFullName } from "@msuite/utils/users";
import { useNavigate } from "react-router";
import { useChatStore } from "@/components/chat/store";
import { client } from "@/hono";

/** Props Interface */
interface UserPopoverProps {
	userId: string;
}

export function UserPopover({ userId }: UserPopoverProps) {
	/** Store */
	const chatStore = useChatStore();

	/** Hooks */
	const popover = usePopoverContext();
	const { t } = useTranslation();
	const navigate = useNavigate();

	/** Query */
	const { data } = useQuery({
		queryKey: ["users", userId ?? ""],
		queryFn: async () => {
			if (!userId) throw new Error("No userId provided");
			const response = await client.users[":userId"].$get({
				param: {
					userId,
				},
			});
			const data = await responseHandler(response);
			return data.user;
		},
	});

	/** Functions */
	function handleOnNavigate() {
		navigate(`/users/${userId}`);
	}

	function handleOnOpenChat() {
		popover.handleOnClose();
		chatStore.handleOnOpen();
	}

	/** Render */
	return (
		<div className="flex flex-col gap-2 w-[450px]">
			<div className="flex flex-row items-center gap-4">
				<Avatar
					imageUrl={data?.image}
					variant="image"
				/>
				<p className="font-semibold">{getFullName(data)}</p>
			</div>
			<Divider className="col-span-2 card-divider" />
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("personnel.fields.email")}</DataGrid.Label>
					<DataGrid.Value>
						<Email>{guardString(data?.email)}</Email>
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Divider />
				<div className="flex flex-row gap-2 col-span-2">
					<Button
						variant="primary"
						onClick={handleOnOpenChat}
						icon={<Icon name="MessageSquare" />}
					>
						{t("chats.send_message")}
					</Button>
					<Button
						variant="white"
						onClick={handleOnNavigate}
						icon={<Icon name="ExternalLink" />}
					>
						{t("general.information")}
					</Button>
				</div>
			</DataGrid>
		</div>
	);
}
